import {bookingAnalyticsURL} from './islander-workshop-booking-analytics.js?v=3cfab84e164607b00d10cdd6cd142183';

export function calendarUrl(source, flow) {
  if (!/^[1-9]\d*$/.test(String(flow))) return null;
  try {
    const url = new URL(source);
    if (url.origin !== 'https://fareharbor.com' || !/^\/embeds\/calendar\/islanderuk\/?$/.test(url.pathname)) return null;
    url.searchParams.set('flow', String(flow));
    return url.href;
  } catch { return null; }
}

const experiment = () => typeof window !== 'undefined' ? window.IslanderWorkshopExperiment : null;
const analytics = () => experiment() || (typeof window !== 'undefined' ? window.IslanderWorkshopAnalytics : null);

// FareHarbor may open checkout from a calendar created before consent was granted.
// Decorate that handoff at use time without reloading the calendar or its selections.
export function calendarCheckoutURL(event, frame, decorate) {
  if (!frame || event.origin !== 'https://fareharbor.com' || event.source !== frame.contentWindow || typeof decorate !== 'function') return undefined;
  let message, source;
  try {
    message = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    if (message?.type !== 'fareharbor.open' || typeof message.url !== 'string') return undefined;
    source = new URL(message.url, 'https://fareharbor.com');
    if (source.origin !== 'https://fareharbor.com' || source.username || source.password) return undefined;
    if (source.pathname.startsWith('/islanderuk/')) source.pathname = '/embeds/book' + source.pathname;
    if (!source.pathname.startsWith('/embeds/book/islanderuk/')) return undefined;
  } catch { return undefined; }
  const destination = decorate(source.href);
  if (destination === null) return null;
  if (!destination || destination === source.href) return undefined;
  const url = new URL(destination);
  const checkoutVariant = message.embeddedCheckoutABTestVariant;
  if (checkoutVariant) {
    if (typeof checkoutVariant !== 'string' || !/^[a-zA-Z0-9_-]{1,40}$/.test(checkoutVariant)) return undefined;
    if (!url.searchParams.has('embedded-checkout')) url.searchParams.set('embedded-checkout', 'yes');
    if (!url.searchParams.has('embedded-checkout-v3')) url.searchParams.set('embedded-checkout-v3', checkoutVariant);
  }
  return url.href;
}

export class WorkshopBooking {
  constructor(root) {
    this.root = root;
    this.frame = root.querySelector('.workshop-booking__calendar iframe');
    this.panel = root.querySelector('[data-calendar-panel]');
    this.status = root.querySelector('[data-booking-status]');
    this.announcement = root.querySelector('[data-booking-announcement]');
    this.placeholder = root.querySelector('[data-booking-placeholder]');
    this.fallback = root.querySelector('[data-booking-fallback]');
    this.retry = root.querySelector('[data-booking-retry]');
    this.choice = null;
    this.timer = null;
    this.wasOpen = false;
    this.inertNodes = [];
    this.source = this.frame?.src;
    this.checkoutBar = document.querySelector('[data-workshop-checkout-bar]');
    this.events = new AbortController();
    for(const type of ['click','auxclick','contextmenu'])root.addEventListener(type,()=>{if(this.fallback&&!experiment())this.fallback.href=bookingAnalyticsURL(window,this.fallback.href);},{capture:true,signal:this.events.signal});
    window.addEventListener('message', event => this.openCalendarCheckout(event), {capture: true, signal: this.events.signal});
    root.addEventListener('change', event => {
      if (event.target.matches('[data-city-flow]')) this.selectCity(event.target);
    }, {signal: this.events.signal});
    this.retry?.addEventListener('click', () => this.selectCity(this.choice, true), {signal: this.events.signal});
    this.frame?.addEventListener('load', () => this.finishLoad(), {signal: this.events.signal});
    document.querySelector('[data-workshop-close]')?.addEventListener('click', () => {
      if (typeof window.FH?.close === 'function') window.FH.close();
    }, {signal: this.events.signal});
    this.checkoutObserver = new MutationObserver(() => this.checkoutState());
    this.checkoutObserver.observe(document.documentElement, {attributes: true, attributeFilter: ['class']});
    this.checkoutState();
    root.dataset.enhanced = 'true';
  }

  message(key, fallback, city = this.choice?.value || '') {
    return String(this.root.dataset[key] || fallback).split('{city}').join(city);
  }

  destroy() {
    clearTimeout(this.timer);
    this.events.abort();
    this.checkoutObserver.disconnect();
    this.checkoutLink?.remove();
    this.setBackgroundInert(false);
    if (this.checkoutBar) this.checkoutBar.hidden = true;
    delete this.root.dataset.enhanced;
  }

  openCalendarCheckout(event) {
    const href = calendarCheckoutURL(event, this.frame, experiment()?.decorateBookingURL || (source=>bookingAnalyticsURL(window,source)));
    if (href === undefined) return;
    if (href === null) {
      event.stopImmediatePropagation();
      this.status.textContent = 'This booking link needs a review. Please reopen the workshop page before continuing.';
      analytics()?.emit('booking_recovery', {result_status:'reference_conflict'});
      return;
    }
    if (typeof window.FH?.autoLightframe !== 'function') return;
    if (this.checkoutLink && this.checkoutAPI !== window.FH) {
      this.checkoutLink.remove();
      this.checkoutLink = null;
    }
    if (!this.checkoutLink) {
      this.checkoutLink = document.createElement('a');
      this.checkoutLink.hidden = true;
      this.checkoutLink.dataset.workshopCalendarHandoff = '';
      this.checkoutLink.tabIndex = -1;
      this.root.append(this.checkoutLink);
    }
    if (this.checkoutAPI !== window.FH) {
      window.FH.autoLightframe({container:this.checkoutLink});
      this.checkoutAPI = window.FH;
    }
    this.checkoutLink.href = href;
    event.stopImmediatePropagation();
    this.checkoutLink.click();
  }

  selectCity(choice, retry = false) {
    if (!choice || (!retry && this.choice === choice)) return;
    this.choice = choice;
    this.panel.hidden = true;
    this.retry.hidden = true;
    this.root.dataset.loading = 'false';
    clearTimeout(this.timer);
    const flow = choice.dataset.cityFlow;
    const original = calendarUrl(this.source, flow);
    const url = original && (experiment()?.decorateBookingURL ? experiment().decorateBookingURL(original,flow) : original);
    let fallback;
    try {
      const destination = new URL(this.fallback.href || 'https://fareharbor.com/embeds/book/islanderuk/?full-items=yes');
      destination.searchParams.set('flow',flow);
      fallback = experiment()?.decorateBookingURL ? experiment().decorateBookingURL(destination.href,flow) : destination.href;
    } catch { fallback = null; }
    if (!retry) analytics()?.emit('city_selected',{city:String(choice.value),flow:String(flow)});
    if (!fallback || (original && !url)) {
      this.root.dataset.attribution = 'conflict';
      this.fallback.setAttribute?.('aria-disabled','true');
      this.status.textContent = 'This booking link needs a review. Please reopen the workshop page before continuing.';
      if (this.announcement) this.announcement.textContent = this.status.textContent;
      analytics()?.emit('booking_recovery',{result_status:'reference_conflict'});
      return;
    }
    this.fallback.href = fallback;
    this.fallback.textContent = this.message('messageBookingLink', 'Open {city} booking');
    this.root.querySelector('[data-selected-city]').textContent = this.message('messageSelectedCity', '{city} · Pick a date and time');
    this.placeholder.hidden = false;
    if (!this.frame || !url) {
      if (this.root.dataset.editorPreview === 'true') {
        this.placeholder.hidden = true;
        this.panel.hidden = false;
        if (this.announcement) this.announcement.textContent = this.root.querySelector('.workshop-booking__editor')?.textContent || '';
        return;
      }
      this.status.textContent = this.message('messageUnavailable', 'Please use the booking link below to see {city} availability.');
      if (this.announcement) this.announcement.textContent = this.status.textContent;
      return;
    }
    this.root.dataset.loading = 'true';
    this.status.textContent = this.message('messageLoading', 'Finding {city} workshop dates…');
    if (this.announcement) this.announcement.textContent = this.status.textContent;
    // Keep FareHarbor's generated frame/name/resize handler. Navigation clears old selections.
    // It stays laid out while invisible, so the native calendar measures its real compact width.
    this.panel.hidden = false;
    this.frame.title = this.message('messageAvailabilityLabel', '{city} workshop availability');
    this.frame.src = url;
    this.timer = setTimeout(() => {
      this.root.dataset.loading = 'failed';
      analytics()?.emit('booking_recovery',{result_status:'calendar_timeout'});
      this.panel.hidden = true;
      this.status.textContent = this.message('messageTimeout', 'The calendar is taking a little longer. Try again or open booking below.');
      this.retry.hidden = false;
      this.retry.textContent = this.message('messageTryAgain', 'Try again');
      if (this.announcement) this.announcement.textContent = this.status.textContent;
    }, 20000);
  }

  finishLoad() {
    if (!this.choice || !this.frame) return;
    // Cross-origin iframe load does not prove availability loaded successfully.
    // Keep recovery available without inspecting FareHarbor's document or data.
    clearTimeout(this.timer);
    this.root.dataset.loading = 'false';
    this.placeholder.hidden = true;
    this.panel.hidden = false;
    this.retry.hidden = false;
    this.retry.textContent = this.message('messageReload', 'Reload calendar');
    if (this.announcement) this.announcement.textContent = this.message('messageLoadedAnnouncement', 'Choose a date in the {city} calendar. If it does not appear, reload it or use the booking link below.');
  }

  setBackgroundInert(open) {
    if (!open) {
      this.inertNodes.forEach(node => { node.inert = false; });
      this.inertNodes = [];
      return;
    }
    const checkout = document.getElementById('fareharbor-lightframe-iframe');
    if (!checkout || !this.checkoutBar) return;
    const protectSiblings = parent => {
      for (const node of parent.children) {
        if (node === checkout || node === this.checkoutBar) continue;
        if (node.contains(checkout) || node.contains(this.checkoutBar)) protectSiblings(node);
        else if (!node.inert) {
          node.inert = true;
          this.inertNodes.push(node);
        }
      }
    };
    protectSiblings(document.body);
  }

  checkoutState() {
    const open = document.documentElement.classList.contains('fareharbor-showing-lightframe');
    if (this.checkoutBar) this.checkoutBar.hidden = !open;
    if (open !== this.wasOpen) this.setBackgroundInert(open);
    if (this.wasOpen && !open) (this.choice || this.root).focus();
    this.wasOpen = open;
  }
}

if (typeof document !== 'undefined') {
  if (typeof window !== 'undefined' && !document.querySelector('[data-workshop-experiment]')) {
    import('./islander-workshop-experiment-telemetry.js?v=3c7f878adc92c0ec8cea1fcc2d7a8072').then(({initializeInteractionTracking}) => {
      if (!document.querySelector('[data-workshop-experiment]')) window.IslanderWorkshopAnalytics = initializeInteractionTracking(window);
    }).catch(() => { /* Calendar and fallback links remain usable without analytics. */ });
  }
  let controller;
  const initialize = () => {
    const root = document.getElementById('workshop-booking');
    if (controller?.root === root) return;
    controller?.destroy();
    controller = root && !root.dataset.enhanced ? new WorkshopBooking(root) : null;
  };
  const ready = typeof window !== 'undefined' && window.IslanderWorkshopExperiment?.ready;
  if (ready) ready.then(initialize); else initialize();
  document.addEventListener('islander:workshop-experiment-ready', initialize);
  document.addEventListener('shopify:section:load', event => {
    if (event.target.querySelector('#workshop-booking')) initialize();
  });
  document.addEventListener('shopify:section:unload', event => {
    if (controller && event.target.contains(controller.root)) {
      controller.destroy();
      controller = null;
    }
  });
}
